import React from "react";
import { Container } from "~/atoms/Container";
import { colors } from "~/utils/colors";
import { breakPoints } from "~/utils/variables";
import logoHorizontal from "~/images/logo-horizontal.svg";
import iconClipboard from "~/images/process/process-01-clipboard.png";
import iconMegaphone from "~/images/process/process-02-megaphone.png";
import iconHandshake from "~/images/process/process-03-handshake.png";
import iconChart from "~/images/process/process-04-chart.png";
import iconDiamond from "~/images/process/process-05-diamond.png";

const STEPS = [
  { id: "01", title: "制度設計", icon: iconClipboard },
  { id: "02", title: "社内への浸透", icon: iconMegaphone },
  {
    id: "03",
    title: (
      <>
        紹介のきっかけ
        <br />
        づくり
      </>
    ),
    icon: iconHandshake,
  },
  { id: "04", title: "効果測定・改善", icon: iconChart },
  {
    id: "05",
    title: (
      <>
        採用チャネル
        <br />
        として定着
      </>
    ),
    icon: iconDiamond,
  },
];

/** リング半径（ステージ幅に対する%） */
const RING_RADIUS = 36;

const polar = (angleDeg: number) => {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: 50 + RING_RADIUS * Math.cos(rad),
    y: 50 + RING_RADIUS * Math.sin(rad),
  };
};

const stepAngle = (index: number) => -90 + (360 / STEPS.length) * index;

/** ステップ中心が収まる矩形。図の外枠をここに合わせて上下左右の余白を揃える */
const STEP_POINTS = STEPS.map((_, index) => polar(stepAngle(index)));
const ORBIT = (() => {
  const xs = STEP_POINTS.map((p) => p.x);
  const ys = STEP_POINTS.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return { minX, minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY };
})();

/** ステップ円の半径（SVG座標）。CSSの --node を変えたら合わせて調整する */
const NODE_RADIUS = 15;
const ARROW_GAP_DEG = 4;

/** 隣り合うステップ円の縁から縁へ、リングに沿って伸びる矢印 */
const ARROWS = (() => {
  const step = 360 / STEPS.length;
  const nodeHalfDeg = (2 * Math.asin(NODE_RADIUS / (2 * RING_RADIUS)) * 180) / Math.PI;
  return STEPS.map((_, index) => {
    const base = stepAngle(index);
    const start = polar(base + nodeHalfDeg + ARROW_GAP_DEG);
    const tipDeg = base + step - nodeHalfDeg - ARROW_GAP_DEG;
    const tip = polar(tipDeg);
    return {
      start,
      tip,
      line: `M ${start.x} ${start.y} A ${RING_RADIUS} ${RING_RADIUS} 0 0 1 ${tip.x} ${tip.y}`,
      head: `translate(${tip.x} ${tip.y}) rotate(${tipDeg + 90})`,
    };
  });
})();

const toOrbit = ({ x, y }: { x: number; y: number }) => ({
  left: `${((x - ORBIT.minX) / ORBIT.width) * 100}%`,
  top: `${((y - ORBIT.minY) / ORBIT.height) * 100}%`,
});

export const Process: React.FC = () => {
  return (
    <>
      <section className="section">
        <Container>
          <div className="layout">
            <div className="intro">
              <p className="eyebrow">制度をつくるだけでは、</p>
              <h2 className="title">紹介は増えません。</h2>
              <p className="lead">
                制度を整えるだけでは、紹介は自然には増えません。
                <br />
                社員への浸透、紹介のきっかけづくり、
                <br />
                改善の継続が必要です。
              </p>
            </div>

            <div className="stage">
              <div className="orbit" style={{ aspectRatio: `${ORBIT.width} / ${ORBIT.height}` }}>
                <svg
                  className="ring"
                  viewBox={`${ORBIT.minX} ${ORBIT.minY} ${ORBIT.width} ${ORBIT.height}`}
                  aria-hidden="true"
                >
                  <defs>
                    {ARROWS.map((arrow, index) => (
                      <linearGradient
                        key={STEPS[index].id}
                        id={`processArrow${index}`}
                        gradientUnits="userSpaceOnUse"
                        x1={arrow.start.x}
                        y1={arrow.start.y}
                        x2={arrow.tip.x}
                        y2={arrow.tip.y}
                      >
                        <stop offset="0%" stopColor={colors.brand} stopOpacity="0" />
                        <stop offset="100%" stopColor={colors.brand} />
                      </linearGradient>
                    ))}
                  </defs>
                  <circle className="ringDisc" cx="50" cy="50" r={RING_RADIUS} />
                  {ARROWS.map((arrow, index) => (
                    <g key={STEPS[index].id} className="arrow">
                      <path className="arrowLine" d={arrow.line} stroke={`url(#processArrow${index})`} />
                      <path className="arrowHead" d="M -1.8 -1.5 L 0 0 L -1.8 1.5" transform={arrow.head} />
                    </g>
                  ))}
                </svg>

                <p className="hub" style={toOrbit({ x: 50, y: 50 })}>
                  このプロセスを
                  <br />
                  <strong>継続的に</strong>
                </p>

                <ol className="steps">
                  {STEPS.map((step, index) => (
                    <li key={step.id} className="step" style={toOrbit(STEP_POINTS[index])}>
                      <img className="stepIcon" src={step.icon} alt="" />
                      <span className="stepTitle">{step.title}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>

          <div className="closing">
            <p className="closingText">
              <span className="closingBrand">
                <img className="brandLogo" src={logoHorizontal} alt="Refcome" />
                は、
              </span>
              <span className="closingLine">
                <span className="phrase">制度設計から定着までを</span>
                <span className="phrase">
                  <span className="marker">一気通貫</span>で支援します。
                </span>
              </span>
            </p>
          </div>
        </Container>
      </section>

      <style jsx>{`
        .section {
          padding: 80px 24px;
          background: ${colors.defaultBg};
        }
        .section :global(.container) {
          display: block;
          width: 100%;
          max-width: 1000px;
        }

        .layout {
          display: grid;
          grid-template-columns: auto 480px;
          justify-content: center;
          align-items: center;
          column-gap: 56px;
        }

        .marker {
          color: ${colors.brand};
          background: linear-gradient(transparent 70%, ${colors.subOrange} 70%);
        }

        /* ---------- 導入 ---------- */
        .eyebrow {
          margin: 0;
          color: ${colors.defaultText};
          font-size: 24px;
          font-weight: bold;
          line-height: 1.5;
        }
        .title {
          margin: 4px 0 0;
          color: ${colors.defaultText};
          font-size: 48px;
          font-weight: bold;
          line-height: 1.3;
          letter-spacing: 0.02em;
          white-space: nowrap;
        }
        .lead {
          margin: 24px 0 0;
          color: ${colors.defaultText};
          font-size: 18px;
          line-height: 1.9;
        }

        /* ---------- サイクル図 ---------- */
        .stage {
          --node: 144px;
          width: 100%;
          max-width: 480px;
          margin: 0 auto;
          padding: calc(var(--node) / 2);
          box-sizing: border-box;
        }
        .orbit {
          position: relative;
          width: 100%;
        }
        .ring {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          overflow: visible;
        }
        .ringDisc {
          fill: rgba(255, 255, 255, 0.7);
        }
        .arrowLine,
        .arrowHead {
          fill: none;
          stroke-width: 0.5;
          stroke-linecap: round;
          stroke-linejoin: round;
        }
        .arrowHead {
          stroke: ${colors.brand};
        }

        .hub {
          position: absolute;
          width: 60%;
          margin: 0;
          transform: translate(-50%, -50%);
          color: ${colors.defaultText};
          font-size: 17px;
          font-weight: bold;
          line-height: 1.5;
          text-align: center;
        }
        .hub strong {
          display: inline-block;
          margin-top: 2px;
          color: ${colors.brand};
          font-size: 30px;
          line-height: 1.3;
        }

        .steps {
          margin: 0;
          padding: 0;
          list-style: none;
        }
        .step {
          position: absolute;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
          width: var(--node);
          height: var(--node);
          padding: 12px;
          border: 1px solid ${colors.subOrange};
          border-radius: 50%;
          background: ${colors.white};
          box-shadow: 0 10px 24px rgba(255, 100, 0, 0.1);
          box-sizing: border-box;
          transform: translate(-50%, -50%);
          text-align: center;
        }
        .stepIcon {
          display: block;
          width: 44px;
          height: 44px;
          margin-bottom: 2px;
          object-fit: contain;
        }
        .stepTitle {
          color: ${colors.defaultText};
          font-size: 15px;
          font-weight: bold;
          line-height: 1.35;
          word-break: keep-all;
        }

        /* ---------- 締め ---------- */
        .closing {
          margin-top: 48px;
          text-align: center;
        }
        .closingText {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          gap: 4px 8px;
          margin: 0;
          color: ${colors.defaultText};
          font-size: 30px;
          font-weight: bold;
          line-height: 1.5;
        }
        .closingBrand {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
        }
        .brandLogo {
          display: block;
          width: 172px;
          height: auto;
        }

        /* ---------- タブレット ---------- */
        @media (${breakPoints.tablet}) {
          .section {
            padding: 64px 24px;
          }
          .layout {
            grid-template-columns: minmax(0, 1fr);
            row-gap: 32px;
          }
          .intro {
            text-align: center;
          }
          .closing {
            margin-top: 32px;
          }
          .closingText {
            font-size: 24px;
          }
          .brandLogo {
            width: 140px;
          }
        }

        /* ---------- スマホ ---------- */
        @media (${breakPoints.sp}) {
          .section {
            padding: 48px 16px;
          }
          .layout {
            row-gap: 24px;
          }
          .eyebrow {
            font-size: 17px;
          }
          .title {
            font-size: 34px;
          }
          .lead {
            margin-top: 12px;
            font-size: 14px;
            line-height: 1.8;
          }

          .stage {
            --node: 104px;
            max-width: 340px;
          }
          .arrowLine,
          .arrowHead {
            stroke-width: 0.65;
          }
          .hub {
            font-size: 12px;
          }
          .hub strong {
            font-size: 19px;
          }
          .step {
            gap: 2px;
            padding: 8px;
            box-shadow: 0 6px 16px rgba(255, 100, 0, 0.1);
          }
          .stepIcon {
            width: 30px;
            height: 30px;
            margin-bottom: 1px;
          }
          .stepTitle {
            font-size: 11px;
            line-height: 1.3;
          }

          .closing {
            margin-top: 24px;
          }
          .closingText {
            flex-direction: column;
            gap: 6px;
            font-size: 20px;
          }
          .brandLogo {
            width: 116px;
          }
          .phrase {
            display: block;
          }
        }
      `}</style>
    </>
  );
};
